import {
  createContext,
  type AnchorHTMLAttributes,
  type MouseEvent,
  type ReactNode,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

interface RouterState {
  pathname: string;
  search: string;
  navigate: (target: string, options?: { replace?: boolean }) => void;
}

const RouterContext = createContext<RouterState | null>(null);
const staticDemo = import.meta.env.VITE_METRICGRAPH_STATIC_DEMO === 'true';

function browserLocation() {
  if (!staticDemo) {
    return { pathname: window.location.pathname, search: window.location.search };
  }
  const hash = window.location.hash.replace(/^#/, '') || '/overview';
  const next = new URL(hash, window.location.origin);
  return { pathname: next.pathname, search: next.search };
}

function linkHref(target: string) {
  return staticDemo ? `#${target}` : target;
}

export function RouterProvider({ children }: { children: ReactNode }) {
  const [location, setLocation] = useState(browserLocation);
  useEffect(() => {
    const update = () => setLocation(browserLocation());
    window.addEventListener('popstate', update);
    window.addEventListener('hashchange', update);
    return () => {
      window.removeEventListener('popstate', update);
      window.removeEventListener('hashchange', update);
    };
  }, []);
  const value = useMemo<RouterState>(
    () => ({
      ...location,
      navigate(target, options) {
        const next = new URL(target, window.location.origin);
        if (staticDemo) {
          const hash = `#${next.pathname}${next.search}`;
          if (options?.replace) window.history.replaceState(null, '', hash);
          else window.location.hash = hash;
        } else if (options?.replace) window.history.replaceState(null, '', next);
        else window.history.pushState(null, '', next);
        setLocation({ pathname: next.pathname, search: next.search });
        window.scrollTo({ top: 0, behavior: 'instant' });
      },
    }),
    [location],
  );
  return <RouterContext.Provider value={value}>{children}</RouterContext.Provider>;
}

function useRouter() {
  const value = useContext(RouterContext);
  if (!value) throw new Error('MetricGraph router context is missing.');
  return value;
}

export function useLocation() {
  const { pathname, search } = useRouter();
  return { pathname, search };
}

export function useNavigate() {
  return useRouter().navigate;
}

interface LinkProps extends Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'href'> {
  to: string;
}

function shouldNavigate(event: MouseEvent<HTMLAnchorElement>) {
  return (
    !event.defaultPrevented &&
    event.button === 0 &&
    !event.metaKey &&
    !event.ctrlKey &&
    !event.shiftKey &&
    !event.altKey
  );
}

export function Link({ to, onClick, children, ...props }: LinkProps) {
  const navigate = useNavigate();
  return (
    <a
      {...props}
      href={linkHref(to)}
      onClick={(event) => {
        onClick?.(event);
        if (shouldNavigate(event)) {
          event.preventDefault();
          navigate(to);
        }
      }}
    >
      {children}
    </a>
  );
}

export function NavLink({
  to,
  activeFor,
  className,
  children,
  ...props
}: Omit<LinkProps, 'className'> & {
  activeFor?: string[];
  className?: string | ((state: { isActive: boolean }) => string);
}) {
  const { pathname } = useLocation();
  const activePaths = activeFor ?? [to];
  const isActive = activePaths.some((path) => pathname === path || pathname.startsWith(`${path}/`));
  return (
    <Link
      {...props}
      to={to}
      className={typeof className === 'function' ? className({ isActive }) : className}
      aria-current={isActive ? 'page' : undefined}
    >
      {children}
    </Link>
  );
}

export function useParams(): { id?: string } {
  const { pathname } = useLocation();
  const match = /^\/catalog\/([^/]+)$/.exec(pathname);
  return match?.[1] ? { id: decodeURIComponent(match[1]) } : {};
}

export function useSearchParams(): [URLSearchParams, (next: Record<string, string>) => void] {
  const { pathname, search, navigate } = useRouter();
  const params = useMemo(() => new URLSearchParams(search), [search]);
  return [
    params,
    (next) => {
      const query = new URLSearchParams(next).toString();
      navigate(`${pathname}${query ? `?${query}` : ''}`);
    },
  ];
}
